import { GroupType, ITreeNodeDto, SystemResponse } from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import { useSystems } from 'custom_hooks/data_hooks/system/useSystems';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createFullTree } from 'shared/data_tree/tree';
import { Tree, TreeNodeData } from 'shared/data_tree/types';
import { Filter } from 'utils/query_v3';

export type FilterMode = 'assetAndAbove' | 'monitoringPointAndAbove' | 'all';

export type GroupTreeNode = Readonly<{
    id: string;
    name: string;
    type: GroupType;
    isCollectorDependentSystem: boolean;
    hasChildren: boolean;
}>;

const isIncludedByMode = (mode: FilterMode, type: GroupType): boolean => {
    switch (mode) {
        case 'assetAndAbove':
            return (
                type === GroupType.System ||
                type === GroupType.Assembly ||
                type === GroupType.Group ||
                type === GroupType.Asset
            );
        case 'monitoringPointAndAbove':
            return (
                isIncludedByMode('assetAndAbove', type) ||
                type === GroupType.MonitoringPoint
            );
        case 'all':
            return true;
    }
};

const canHaveVisibleChildren = (mode: FilterMode, type: GroupType): boolean => {
    switch (mode) {
        case 'assetAndAbove':
            return (
                type === GroupType.System ||
                type === GroupType.Assembly ||
                type === GroupType.Group
            );
        case 'monitoringPointAndAbove':
            return (
                canHaveVisibleChildren('assetAndAbove', type) || type === GroupType.Asset
            );
        case 'all':
            return true;
    }
};

function toTreeNodeData(
    dto: ITreeNodeDto,
    mode: FilterMode,
): TreeNodeData<GroupTreeNode> {
    const filteredChildren = dto.Children.filter((c) =>
        isIncludedByMode(mode, c.TreeItemType),
    );
    return {
        id: dto.Id,
        data: {
            id: dto.Id,
            name: dto.Name,
            type: dto.TreeItemType,
            isCollectorDependentSystem: dto.IsCollectorDependentSystem,
            hasChildren:
                canHaveVisibleChildren(mode, dto.TreeItemType) &&
                filteredChildren.length > 0,
        },
        children: filteredChildren.map((c) => toTreeNodeData(c, mode)),
    };
}

function systemToTreeNodeData(system: SystemResponse): TreeNodeData<GroupTreeNode> {
    return {
        id: system.Id,
        data: {
            id: system.Id,
            name: system.Name,
            type: GroupType.System,
            isCollectorDependentSystem: system.IsCollectorDependentSystem,
            hasChildren: true,
        },
    };
}

type UseGroupDropdownTreeOptions = Readonly<{
    locationId: string;
    mode: FilterMode;
}>;

export function useGroupDropdownTree({ locationId, mode }: UseGroupDropdownTreeOptions) {
    const emptyTree = useMemo(
        () =>
            createFullTree<GroupTreeNode, GroupTreeNode>(
                [],
                (n) => n.id,
                () => undefined,
                (n) => n,
            ),
        [],
    );
    const [tree, _setTree] = useState<Tree<GroupTreeNode>>(emptyTree);
    const setTree = useCallback(
        (updater: (current: Tree<GroupTreeNode>) => Tree<GroupTreeNode>) => {
            _setTree((cur) => updater(cur));
        },
        [],
    );

    const [loadingIds, setLoadingIds] = useState<string[]>([]);

    const { data: systemsData, isLoading: isLoadingSystems } = useSystems({
        filter: locationId ? Filter.and(Filter.eq('LocationId', locationId)) : undefined,
        pageLimit: 1000,
    });

    useEffect(() => {
        if (!systemsData) {
            return;
        }
        const roots = systemsData.Result.map(systemToTreeNodeData);
        setTree(() =>
            createFullTree<TreeNodeData<GroupTreeNode>, GroupTreeNode>(
                roots,
                (n) => n.id,
                (n) => n.children,
                (n) => n.data,
            ),
        );
    }, [systemsData, setTree]);

    const loadChildren = useCallback(
        (nodeId: string) => {
            setLoadingIds((prev) => [...prev, nodeId]);
            sdClient
                .getTree({ treeItemId: nodeId })
                .then((response) => {
                    const children = response.Tree.Children.filter((c) =>
                        isIncludedByMode(mode, c.TreeItemType),
                    ).map((c) => toTreeNodeData(c, mode));

                    setTree((current) =>
                        current.replaceChildren({ id: nodeId, childData: children }),
                    );
                })
                .finally(() => {
                    setLoadingIds((prev) => prev.filter((id) => id !== nodeId));
                });
        },
        [mode, setTree],
    );

    return {
        tree,
        isLoading: isLoadingSystems,
        loadingIds,
        loadChildren,
    };
}
